import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { SectionHeader } from "../shared/SectionHeader";
import { SettingsInput, labelClass } from "../shared/SettingsInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Copy, Check, ShieldCheck } from "lucide-react";
import { PASSWORD_REQUIREMENTS, validatePassword } from "@/lib/password";

const ISSUER = "ReviewBoost";

/** Turn Supabase QR response into an img src (handles raw SVG or data URL). */
function qrToImgSrc(qr: string): string {
  if (!qr) return "";
  if (qr.startsWith("data:")) return qr;
  if (qr.startsWith("http")) return qr;
  const trimmed = qr.trim();
  if (trimmed.startsWith("<")) {
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(trimmed);
  }
  return qr;
}

/** Build QR image URL from TOTP secret when API doesn't return one (otpauth URI → QR server). */
function qrUrlFromSecret(secret: string, label: string): string {
  const encoded = encodeURIComponent(`otpauth://totp/${ISSUER}:${label}?secret=${secret}&issuer=${ISSUER}`);
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encoded}`;
}

/** QR image URL from full otpauth URI (e.g. when API returns uri but not image). */
function qrUrlFromUri(otpauthUri: string): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUri)}`;
}

export function SecuritySettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [factorId, setFactorId] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.mfa.listFactors();
      const hasTotp = (data?.totp?.length ?? 0) > 0;
      setMfaEnrolled(hasTotp);
    })();
  }, []);

  const startEnrollment = () => {
    setEnrollOpen(true);
    setFactorId("");
    setQrCode("");
    setSecret("");
    setVerifyCode("");
    setVerifyError("");
  };

  useEffect(() => {
    if (!enrollOpen || factorId) return;
    setEnrollLoading(true);
    supabase.auth.mfa
      .enroll({ factorType: "totp", friendlyName: "ReviewBoost" })
      .then(({ data, error }) => {
        setEnrollLoading(false);
        if (error) {
          toast({ title: "2FA setup failed", description: error.message, variant: "destructive" });
          setEnrollOpen(false);
          return;
        }
        if (data?.id) setFactorId(data.id);
        const totp = data?.totp as {
          qr_code?: string;
          qrCode?: string;
          secret?: string;
          uri?: string;
        } | undefined;
        const qr = totp?.qr_code ?? totp?.qrCode ?? "";
        if (qr) setQrCode(qr);
        if (totp?.secret) setSecret(totp.secret);
        if (!qr && totp?.uri) setQrCode("url:" + totp.uri);
      })
      .catch(() => {
        setEnrollLoading(false);
        setEnrollOpen(false);
      });
  }, [enrollOpen, toast]);

  const handleCopySecret = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      toast({ title: "Copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const handleVerifyAndEnable = async () => {
    const code = verifyCode.replace(/\s/g, "").slice(0, 6);
    if (code.length !== 6) {
      setVerifyError("Enter the 6-digit code from your app.");
      return;
    }
    setVerifyError("");
    setVerifying(true);
    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
    if (challengeError) {
      setVerifyError(challengeError.message);
      setVerifying(false);
      return;
    }
    const challengeId = challengeData?.id;
    if (!challengeId) {
      setVerifyError("Could not start verification.");
      setVerifying(false);
      return;
    }
    const { error: verifyErrorResult } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code,
    });
    setVerifying(false);
    if (verifyErrorResult) {
      setVerifyError(verifyErrorResult.message || "Invalid code. Try again.");
      return;
    }
    toast({ title: "2FA enabled", description: "Your account is now protected with two-factor authentication." });
    setMfaEnrolled(true);
    setEnrollOpen(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      toast({ title: "Choose a stronger password", description: validationError, variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    setPasswordError("");
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setChangingPassword(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Password updated" });
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
  };

  return (
    <>
      <SectionHeader
        title="Security"
        description="Two-factor authentication and password."
      />
      <Card className="bg-card border-border mb-6">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Two-Factor Authentication</h3>
          <p className="text-[12px] text-muted-foreground">
            Add an extra layer of security using an authenticator app (e.g. Google Authenticator, Authy, Microsoft Authenticator).
          </p>
        </CardHeader>
        <CardContent>
          {mfaEnrolled ? (
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="text-[13px] text-foreground">Enabled — you’ll be asked for a code from your app when you sign in.</span>
            </div>
          ) : (
            <Button onClick={startEnrollment}>Enable 2FA</Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={enrollOpen} onOpenChange={(open) => !verifying && setEnrollOpen(open)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Set up authenticator app</DialogTitle>
            <DialogDescription>
              Scan the QR code with your app, then enter the 6-digit code to verify.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {enrollLoading ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Preparing QR code…</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center gap-3">
                  <p className="text-sm font-medium text-foreground">1. Scan this QR code</p>
                  {(qrCode || secret) ? (
                    <div className="rounded-lg border border-border bg-white p-3">
                      <img
                        src={
                          qrCode
                            ? qrCode.startsWith("url:")
                              ? qrUrlFromUri(qrCode.slice(4))
                              : qrToImgSrc(qrCode)
                            : qrUrlFromSecret(secret, user?.email ?? "user")
                        }
                        alt="QR code for authenticator app"
                        className="h-40 w-40"
                        width={160}
                        height={160}
                      />
                    </div>
                  ) : (
                    <div className="h-40 w-40 rounded-lg border border-dashed border-border bg-muted flex items-center justify-center text-sm text-muted-foreground">
                      No QR code
                    </div>
                  )}
                </div>
                {secret && (
                  <div className="space-y-1.5">
                    <p className="text-sm font-medium text-foreground">Can&apos;t scan? Enter this code manually</p>
                    <div className="flex gap-2">
                      <code className="flex-1 rounded-md border border-border bg-muted px-3 py-2 text-xs text-foreground font-mono break-all select-all">
                        {secret}
                      </code>
                      <Button type="button" variant="outline" size="icon" onClick={handleCopySecret} title="Copy">
                        {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="mfa-verify-code" className="text-sm font-medium text-foreground">
                    2. Enter the 6-digit code from your app
                  </Label>
                  <input
                    id="mfa-verify-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-[16px] tracking-[0.5em] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  {verifyError && (
                    <p className="text-sm text-destructive">{verifyError}</p>
                  )}
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEnrollOpen(false)}
              disabled={verifying || enrollLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleVerifyAndEnable}
              disabled={enrollLoading || verifying || !factorId || verifyCode.replace(/\D/g, "").length !== 6}
            >
              {verifying ? "Verifying…" : "Verify and enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="bg-card border-border mb-6">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Change Password</h3>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="mx-auto max-w-md space-y-4">
            <div className="text-center space-y-2">
              <h4 className="text-xl font-semibold text-foreground">New Password</h4>
              <p className="text-sm text-muted-foreground">
                Please choose a new password. Use upper and lower-case letters, numbers, and special characters for a strong password.
              </p>
            </div>
            <div className="space-y-3 rounded-2xl bg-muted/35 p-4">
              <div className="space-y-2">
                <Label htmlFor="settings-new-password" className="sr-only">New Password</Label>
                <SettingsInput
                  id="settings-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordError("");
                  }}
                  placeholder="New Password"
                  className="h-12 rounded-xl border-0 bg-background px-4 text-base shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="settings-confirm-password" className="sr-only">Confirm Password</Label>
                <SettingsInput
                  id="settings-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError("");
                  }}
                  placeholder="Confirm Password"
                  className="h-12 rounded-xl border-0 bg-background px-4 text-base shadow-sm"
                />
              </div>
            </div>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {PASSWORD_REQUIREMENTS.map((requirement) => (
                <li key={requirement}>• {requirement}</li>
              ))}
            </ul>
            {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
            <Button type="submit" className="w-full" disabled={changingPassword}>
              {changingPassword ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card className="bg-card border-border">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Active Sessions</h3>
        </CardHeader>
        <CardContent>
          <p className="text-[12px] text-muted-foreground">Current device — This session. Revoke others from Supabase Dashboard if needed.</p>
        </CardContent>
      </Card>
    </>
  );
}
